<script setup lang="ts">
import { reactive, ref, watch } from "vue";
import { getLegacyApp } from "../../core/legacy-bridge";
import { projectLibrary } from "../../data";
import { t } from "../../i18n";
import { appStore } from "../../stores/app";
import { Project } from "../../types/type_def";
import { FormatHelper } from "../../utils/format";
import AppModal from "../common/AppModal.vue";
import { loadProjects } from "./projectList";

const editedProject = ref<Project | null>(null);
const editForm = reactive({ name: "", phone: "", seller: "", status: 1 });
const createForm = reactive({ name: "", phone: "", seller: "" });

// Edit: loads the project every time the modal opens. Create: starts with empty fields.
watch(() => appStore.modal, async modal => {
    if (modal === "editProject") {
        editedProject.value = null;
        const project = await projectLibrary.getProject(appStore.editProjectId);
        editedProject.value = project;
        Object.assign(editForm, { name: project.name, phone: project.phone, seller: project.seller, status: Number(project.status) });
    }
    if (modal === "createProject") {
        Object.assign(createForm, { name: "", phone: "", seller: "" });
    }
});

async function saveProject() {
    const project = editedProject.value;
    if (!project) return;
    Object.assign(project, editForm);
    await projectLibrary.saveProject(project);
    appStore.modal = null;
    await loadProjects();
}

// The new project is only saved later, together with its first configuration
function createProject() {
    const project = { ...createForm, status: 1, configurations: [] } as Project;
    appStore.modal = null;
    getLegacyApp().startNewProject(project);
}
</script>

<template>
    <AppModal class="editproject-modal" name="editProject" :title="t('modal.editproject.header')" outer-close>
        <form id="editprojectform" class="form" @submit.prevent="saveProject">
            <div class="is-flex is-justify-content-center is-align-items-center is-full mb-6">
                <div class="is-flex">
                    <label class="label mr-5">
                        <span v-html="t('modal.editproject.confignumber')"></span><span>:</span>
                    </label>
                </div>
                <span id="project-identifier">{{ editedProject?.identifier }}</span>
            </div>
            <div class="field is-horizontal">
                <div class="field-label">
                    <label class="label"><span v-html="t('modal.editproject.projectname')"></span><span>*:</span></label>
                </div>
                <div class="field-body">
                    <div class="simple-input field">
                        <p class="control is-expanded">
                            <input id="project-name" v-model="editForm.name" required class="input is-shadowless is-radiusless" type="text">
                        </p>
                    </div>
                </div>
            </div>
            <div class="field is-horizontal">
                <div class="field-label">
                    <label class="label"><span v-html="t('common.phonenumber')"></span><span>:</span></label>
                </div>
                <div class="field-body">
                    <div class="simple-input field">
                        <p class="control is-expanded">
                            <input id="project-phone" v-model="editForm.phone" class="input is-shadowless is-radiusless" type="text">
                        </p>
                    </div>
                </div>
            </div>
            <div class="field is-horizontal">
                <div class="field-label">
                    <label class="label"><span v-html="t('common.email')"></span><span>:</span></label>
                </div>
                <div class="field-body">
                    <div class="simple-input field">
                        <p class="control is-expanded">
                            <input id="project-email" v-model="editForm.seller" class="input is-shadowless is-radiusless" type="text">
                        </p>
                    </div>
                </div>
            </div>
            <div class="field is-horizontal mb-6">
                <div class="field-label">
                    <label class="label"><span v-html="t('common.status')"></span>:</label>
                </div>
                <div class="field-body">
                    <div class="simple-input field">
                        <div class="control is-expanded">
                            <div class="select simple-select">
                                <select id="project-status" v-model.number="editForm.status" class="is-shadowless is-radiusless">
                                    <option :value="0" v-html="t('common.statuses.inactive')"></option>
                                    <option :value="1" v-html="t('common.statuses.active')"></option>
                                    <option :value="2" v-html="t('common.statuses.ordered')"></option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="field mb-6">
                <div class="control is-flex is-justify-content-center">
                    <button id="save-project" class="button is-default is-dark is-radiusless" type="submit">
                        <span v-html="t('modal.editproject.save')"></span>
                    </button>
                </div>
            </div>
            <div class="field">
                <div class="is-flex is-justify-content-space-between">
                    <div class="field is-flex">
                        <div class="field-label">
                            <label class="label"><span v-html="t('modal.editproject.dateadded')"></span><span>:</span></label>
                        </div>
                        <span id="project-added">{{ editedProject ? FormatHelper.formatDate(editedProject.added) : "" }}</span>
                    </div>
                    <div class="field is-flex">
                        <div class="field-label">
                            <label class="label"><span v-html="t('modal.editproject.datemodified')"></span><span>:</span></label>
                        </div>
                        <span id="project-modified">{{ editedProject ? FormatHelper.formatDate(editedProject.modified) : "" }}</span>
                    </div>
                </div>
            </div>
        </form>
    </AppModal>

    <AppModal class="createproject-modal" name="createProject" :title="t('modal.newproject.header')" outer-close>
        <form id="createprojectform" class="form" @submit.prevent="createProject">
            <div class="field is-horizontal">
                <div class="field-label is-normal">
                    <label class="label"><span v-html="t('modal.newproject.projectname')"></span><span>*:</span></label>
                </div>
                <div class="field-body">
                    <div class="simple-input field">
                        <p class="control is-expanded">
                            <input id="newproject-name" v-model="createForm.name" required class="input is-shadowless is-radiusless" type="text">
                        </p>
                    </div>
                </div>
            </div>
            <div class="field is-horizontal">
                <div class="field-label is-normal">
                    <label class="label"><span v-html="t('common.phonenumber')"></span><span>:</span></label>
                </div>
                <div class="field-body">
                    <div class="simple-input field">
                        <p class="control is-expanded">
                            <input id="newproject-phone" v-model="createForm.phone" class="input is-shadowless is-radiusless" type="text">
                        </p>
                    </div>
                </div>
            </div>
            <div class="field is-horizontal">
                <div class="field-label is-normal">
                    <label class="label"><span v-html="t('common.email')"></span><span>:</span></label>
                </div>
                <div class="field-body">
                    <div class="simple-input field">
                        <p class="control is-expanded">
                            <input id="newproject-email" v-model="createForm.seller" class="input is-shadowless is-radiusless" type="text">
                        </p>
                    </div>
                </div>
            </div>
            <div class="field mb-6">
                <div class="control is-flex is-justify-content-center">
                    <button id="create-project" class="button is-default is-dark is-radiusless" type="submit">
                        <span v-html="t('modal.newproject.save')"></span>
                    </button>
                </div>
            </div>
        </form>
    </AppModal>
</template>
